/**
 * useWishlistProximity.ts
 *
 * On app open, checks user's geolocation against all unnotified
 * visit_wishlist entries. If any saved shop is within 0.5 miles,
 * sends a push notification and marks it as notified.
 *
 * Usage: call once in App.tsx after auth is confirmed.
 */

import { useEffect } from 'react'
import { supabase } from './supabase'
import { sendPushToUser } from './push'

// Haversine formula — returns distance in miles
function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8 // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// Opt-in, foreground-only: using the position already read on app open, record
// the user's presence at the shop they're at (for "friends here") and log any
// nearby shops as visit candidates (for later rating nudges). Fire-and-forget,
// and a no-op unless the user has turned the matching preference on.
async function handlePresenceAndVisits(userId: string, lat: number, lng: number) {
  try {
    const { data: prefs } = await supabase
      .from('profiles')
      .select('share_presence, rating_nudges')
      .eq('id', userId)
      .single()
    if (!prefs || (!prefs.share_presence && !prefs.rating_nudges)) return

    // Bounding box of roughly ±300m, then an exact distance filter.
    const d = 0.0027
    const { data: shops } = await supabase
      .from('coffee_shops')
      .select('id, lat, lng')
      .eq('is_active', true)
      .gte('lat', lat - d).lte('lat', lat + d)
      .gte('lng', lng - d).lte('lng', lng + d)
      .limit(50)

    const near = (shops || [])
      .filter((s: any) => s.lat != null && s.lng != null)
      .map((s: any) => ({ id: s.id, dist: haversineMiles(lat, lng, s.lat, s.lng) }))
      .filter(s => s.dist <= 0.1) // ~160m — close enough to count as "at" the shop
      .sort((a, b) => a.dist - b.dist)

    if (near.length === 0) return

    if (prefs.rating_nudges) {
      await supabase.rpc('record_visit_candidates', { p_shop_ids: near.map(s => s.id) })
    }
    if (prefs.share_presence) {
      await supabase.rpc('set_presence', { p_shop_id: near[0].id })
    }
  } catch {
    // best-effort — never disrupt app open
  }
}

export function useWishlistProximity(userId: string | null) {
  useEffect(() => {
    if (!userId) return
    if (!navigator.geolocation) return

    // Only run once per session using sessionStorage flag
    const sessionKey = `sb_wishlist_check_${userId}`
    if (sessionStorage.getItem(sessionKey)) return
    sessionStorage.setItem(sessionKey, '1')

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: userLat, longitude: userLng } = pos.coords

        // Opt-in: record presence + visit candidates from this same position.
        handlePresenceAndVisits(userId, userLat, userLng)

        // Fetch all unnotified wishlist entries for this user
        const { data: wishlist, error } = await supabase
          .from('visit_wishlist')
          .select('id, shop_name, lat, lng')
          .eq('user_id', userId)
          .eq('notified', false)

        if (error || !wishlist || wishlist.length === 0) return

        for (const entry of wishlist) {
          if (!entry.lat || !entry.lng) continue
          const dist = haversineMiles(userLat, userLng, entry.lat, entry.lng)
          if (dist <= 0.5) {
            // Send push notification
            await sendPushToUser(
              userId,
              `You're near ${entry.shop_name} ☕`,
              "It's on your visit wishlist — now's your chance!",
              { type: 'wishlist_proximity', shopName: entry.shop_name }
            )
            // Mark as notified so it doesn't fire again
            await supabase
              .from('visit_wishlist')
              .update({ notified: true })
              .eq('id', entry.id)
          }
        }
      },
      () => { /* Geolocation denied or unavailable — fail silently */ },
      { timeout: 5000, maximumAge: 60000 }
    )
  }, [userId])
}
